/**
 * Third-Party Widget Lazy Loader
 *
 * Performance optimization for homepage third-party widgets:
 * - Judge.me reviews carousel (~150KB)
 * - Purebrand banner (~150KB)
 *
 * Total savings: ~300KB initial load
 * Expected improvement: +1-2s TTI
 *
 * Strategy: Use Intersection Observer to load widgets only when they scroll into view
 */

(function () {
  'use strict';

  // Configuration
  const CONFIG = {
    rootMargin: '200px', // Start loading 200px before widget enters viewport
    threshold: 0.01, // Trigger when 1% visible
    loadDelay: 100, // Small delay to batch multiple widgets
  };

  // Timing constants for Judge.me review truncation detection
  const RENDER_DELAY_MS = 500; // Wait for carousel to render after DOM mutation
  const INITIAL_LOAD_DELAY_MS = 1000; // Wait for initial Judge.me content load
  const RESIZE_DEBOUNCE_MS = 250; // Debounce window resize events for performance

  // Track promises for each script to prevent duplicates and enable concurrent calls
  const scriptPromises = new Map();

  /**
   * Load a script dynamically
   */
  function loadScript(src) {
    // If a promise for this script already exists, return it
    if (scriptPromises.has(src)) {
      return scriptPromises.get(src);
    }

    // Create new promise, store it, and start loading
    const promise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;

      script.onload = () => {
        if (typeof Shopify !== 'undefined' && Shopify.designMode) {
          console.log('🚀 Lazy loaded script:', src);
        }
        resolve();
      };

      script.onerror = () => {
        console.error('❌ Failed to lazy load script:', src);
        // Remove failed promise to allow retries
        scriptPromises.delete(src);
        reject(new Error(`Failed to load ${src}`));
      };

      document.head.appendChild(script);
    });

    scriptPromises.set(src, promise);
    return promise;
  }

  /**
   * Detect text truncation and add data-truncated attribute
   * Enables "Read more" overlay for Judge.me carousel reviews
   * NOTE: This function is tightly coupled to the Judge.me widget's DOM structure.
   * If Judge.me updates their HTML, these selectors (.jdgm-card, .jdgm-text) may need to be updated.
   */
  function detectTruncatedReviews() {
    const cards = document.querySelectorAll('.jdgm-card');

    cards.forEach((card) => {
      const textElement = card.querySelector('.jdgm-text');

      if (textElement) {
        // Check if text is truncated by comparing scroll height to client height
        const isTruncated =
          textElement.scrollHeight > textElement.clientHeight + 2; // +2px tolerance

        if (isTruncated) {
          card.setAttribute('data-truncated', 'true');
        } else {
          card.removeAttribute('data-truncated');
        }
      }
    });
  }

  /**
   * Load Judge.me widgets
   */
  function loadJudgemeWidget(widget) {
    // Judge.me already has smart loading via judgeme_core.liquid
    // But we can manually trigger initialization if needed
    const judgemeElements = widget.querySelectorAll(
      '.jdgm-widget, .jdgm-carousel-wrapper'
    );

    if (judgemeElements.length > 0) {
      if (typeof Shopify !== 'undefined' && Shopify.designMode) {
        console.log('⭐ Judge.me widgets detected');
      }

      // Trigger Judge.me initialization if not already loaded
      if (window.jdgm && typeof window.jdgm.loadJS === 'function') {
        // Judge.me core is loaded, widgets will auto-initialize
        if (typeof Shopify !== 'undefined' && Shopify.designMode) {
          console.log('⭐ Judge.me core ready, widgets will auto-initialize');
        }
      } else {
        if (typeof Shopify !== 'undefined' && Shopify.designMode) {
          console.log('⭐ Waiting for Judge.me core...');
        }
      }

      // Set up MutationObserver to detect when Judge.me dynamically adds carousel content
      const observer = new MutationObserver(() => {
        // Wait for carousel to render, then detect truncated reviews
        setTimeout(() => {
          detectTruncatedReviews();
        }, RENDER_DELAY_MS);
      });

      // Observe the widget for DOM changes (Judge.me adds content dynamically)
      observer.observe(widget, {
        childList: true,
        subtree: true,
      });

      // Also run detection after a delay in case content is already loaded
      setTimeout(() => {
        detectTruncatedReviews();
      }, INITIAL_LOAD_DELAY_MS);
    }
  }

  /**
   * Load Purebrand widgets
   */
  function loadPurebrandWidget(widget) {
    // Purebrand widgets are typically Shopify app blocks
    // They may have their own loading logic
    const purebrandElements = widget.querySelectorAll(
      '[class*="purebrand"], [id*="purebrand"]'
    );

    if (purebrandElements.length > 0) {
      if (typeof Shopify !== 'undefined' && Shopify.designMode) {
        console.log('💎 Purebrand widgets detected');
      }
      // Purebrand typically loads via Shopify app proxy
      // No explicit script loading needed, just visibility trigger
    }
  }

  /**
   * Initialize widget when it becomes visible
   */
  function initializeWidget(widget) {
    const widgetType = widget.dataset.lazyWidget;

    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      console.log(`🎯 Initializing lazy widget: ${widgetType}`);
    }

    // Remove lazy-loading class to trigger CSS transitions
    widget.classList.remove('lazy-widget-pending');
    widget.classList.add('lazy-widget-loading');

    // Load appropriate widget
    switch (widgetType) {
      case 'judgeme':
        loadJudgemeWidget(widget);
        break;
      case 'purebrand':
        loadPurebrandWidget(widget);
        break;
      default:
        // Generic widget with data-widget-src attribute
        const scriptSrc = widget.dataset.widgetSrc;
        if (scriptSrc) {
          loadScript(scriptSrc).then(() => {
            if (typeof Shopify !== 'undefined' && Shopify.designMode) {
              console.log('✅ Generic widget script loaded');
            }
          });
        }
    }

    // Mark as loaded after a short delay
    setTimeout(() => {
      widget.classList.remove('lazy-widget-loading');
      widget.classList.add('lazy-widget-loaded');
    }, CONFIG.loadDelay);
  }

  /**
   * Create Intersection Observer for lazy loading
   */
  function createLazyLoader() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const widget = entry.target;

            // Unobserve immediately to prevent multiple triggers
            observer.unobserve(widget);

            // Initialize widget with small delay
            setTimeout(() => {
              initializeWidget(widget);
            }, CONFIG.loadDelay);
          }
        });
      },
      {
        rootMargin: CONFIG.rootMargin,
        threshold: CONFIG.threshold,
      }
    );

    return observer;
  }

  /**
   * Initialize lazy loading system
   */
  function init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
      return;
    }

    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      console.log('🎬 Initializing third-party lazy loader...');
    }

    // Create observer
    const lazyLoader = createLazyLoader();

    // Find all lazy widgets
    const lazyWidgets = document.querySelectorAll('[data-lazy-widget]');

    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      console.log(`🔍 Found ${lazyWidgets.length} lazy widgets`);
    }

    // Observe each widget
    lazyWidgets.forEach((widget) => {
      widget.classList.add('lazy-widget-pending');
      lazyLoader.observe(widget);
    });

    // Fallback: Load all widgets after 10 seconds if still not loaded
    setTimeout(() => {
      const unloadedWidgets = document.querySelectorAll('.lazy-widget-pending');
      if (unloadedWidgets.length > 0) {
        if (typeof Shopify !== 'undefined' && Shopify.designMode) {
          console.log(
            `⏰ Fallback: Force loading ${unloadedWidgets.length} unloaded widgets`
          );
        }
        unloadedWidgets.forEach((widget) => {
          lazyLoader.unobserve(widget);
          initializeWidget(widget);
        });
      }
    }, 10000);

    // Re-detect truncation on window resize (debounced for performance)
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        detectTruncatedReviews();
      }, RESIZE_DEBOUNCE_MS);
    });
  }

  // Start the magic! ✨
  init();
})();
